import React from 'react';
import './Typography.css';

export type WxbHeadingLevel = 1 | 2 | 3 | 4;
export type WxbTextTone = 'primary' | 'secondary' | 'muted' | 'success' | 'warning' | 'danger' | 'info';
export type WxbTextSize = 'xs' | 'sm' | 'md' | 'lg';
export type WxbTextWeight = 'regular' | 'medium' | 'semibold';

export interface WxbHeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  level?: WxbHeadingLevel;
  eyebrow?: React.ReactNode;
  meta?: React.ReactNode;
}

export interface WxbTextProps extends React.HTMLAttributes<HTMLSpanElement> {
  as?: 'span' | 'p' | 'div';
  tone?: WxbTextTone;
  size?: WxbTextSize;
  weight?: WxbTextWeight;
  truncate?: boolean;
}

export interface WxbCodeTextProps extends React.HTMLAttributes<HTMLElement> {
  compact?: boolean;
}

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

export const WxbHeading: React.FC<WxbHeadingProps> = ({
  level = 3,
  eyebrow,
  meta,
  className = '',
  children,
  ...props
}) => {
  const headingClassName = joinClasses('wxb-heading', `wxb-heading-${level}`);
  const heading =
    level === 1 ? (
      <h1 className={headingClassName} {...props}>{children}</h1>
    ) : level === 2 ? (
      <h2 className={headingClassName} {...props}>{children}</h2>
    ) : level === 4 ? (
      <h4 className={headingClassName} {...props}>{children}</h4>
    ) : (
      <h3 className={headingClassName} {...props}>{children}</h3>
    );

  return (
    <div className={joinClasses('wxb-heading-wrap', className)}>
      {eyebrow && <div className="wxb-heading-eyebrow">{eyebrow}</div>}
      {heading}
      {meta && <div className="wxb-heading-meta">{meta}</div>}
    </div>
  );
};

export const WxbText: React.FC<WxbTextProps> = ({
  as = 'span',
  tone = 'primary',
  size = 'md',
  weight = 'regular',
  truncate = false,
  className = '',
  children,
  ...props
}) => {
  const TextTag = as;

  return (
    <TextTag
      className={joinClasses(
        'wxb-text',
        `wxb-text-${tone}`,
        `wxb-text-${size}`,
        `wxb-text-${weight}`,
        truncate && 'wxb-text-truncate',
        className,
      )}
      {...props}
    >
      {children}
    </TextTag>
  );
};

export const WxbCodeText: React.FC<WxbCodeTextProps> = ({
  compact = false,
  className = '',
  children,
  ...props
}) => (
  <code
    className={joinClasses('wxb-code-text', compact && 'wxb-code-text-compact', className)}
    {...props}
  >
    {children}
  </code>
);
